import React, { useEffect, useRef, useState } from "react"
import clsx from "clsx"
import { ICreateCandidatResponseModel } from "../../core/_models"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import MyModal from "@common-utils/MyModal"
import MyTooltip from "@common-utils/MyTooltip"
import { useNavigate } from "react-router-dom"

// Import des formulaires et helpers
import CandidatAddSuiviMessageRepondeurForm from "../../../suivi/_CandidatAddSuiviMessageRepondeur"
import CandidatAddSuiviTelForm from "../../../suivi/tel/CandidatAddSuiviTelForm"
import CandidatAddToViviersForm from "../../../actions/_CandidatAddToViviers"
import CandidatDemanderInfoPreEmbaucheParMail from "../../../actions/_CandidatDemanderInfoPreEmbaucheParMail"
import CandidatEntamerDemandeEmbauche from "../../../actions/_CandidatEntamerDemandeEmbauche"
import { addCandidatesToListeTraitement, removeCandidatesFromListeTraitement } from "../../../core/_requests"
import { useAuth } from "../../../../../../AppContext"
import { CandidatsRechercheResultsPreviewHelpersDocos } from "../../../recherche/results/_preview/helpers/_CandidatsRechercheResultsPreviewHelpersDocos"
import { CONST_CANDIDAT_TYPE_SAISI } from "@common-constants/*"
import ProgrammerReunion from "../../../actions/programmerReunion/ProgrammerReunion"

interface Props {
     item: ICreateCandidatResponseModel & {
          localDoneActions?: string[]
          localIsInProcessingList?: boolean
          localIsRecrutementEntame?: boolean
     }
     onPreviewCV: () => void
     onUpdate: (updatedFields: Partial<Props["item"]>) => void // Pour la persistance localStorage
}

const Step3ManageCandidate = ({ item, onPreviewCV, onUpdate }: Props) => {
     const navigate = useNavigate()
     const { setCurrentUser } = useAuth()
     const [ongoingAction, setOngoingAction] = useState<string | undefined>()

     const [isRecrutementEntame, setIsRecrutementEntame] = useState<boolean>(item.localIsRecrutementEntame || false)
     const [isInProcessingList, setIsInProcessingList] = useState(item.localIsInProcessingList || false)
     const [doneActions, setDoneActions] = useState<string[]>(item.localDoneActions || [])

     const [isDropdownOpen, setIsDropdownOpen] = useState(false)
     const dropdownRef = useRef<HTMLDivElement>(null)
     const cvUrl = FileAndDriveHelper.getUrlOfDriveFilePreview(item.curriculum.googleDriveID as string)

     // SYNC STATE WITH PROPS: Required for "Bulk Select All" functionality
     useEffect(() => {
          setIsInProcessingList(item.localIsInProcessingList || false)
     }, [item.localIsInProcessingList])

     useEffect(() => {
          setIsRecrutementEntame(item.localIsRecrutementEntame || false)
     }, [item.localIsRecrutementEntame])

     const markAsDone = (action: string) => {
          const newActions = [...doneActions]
          if (!newActions.includes(action)) {
               newActions.push(action)
               setDoneActions(newActions)
               onUpdate({ localDoneActions: newActions })
          }
          setOngoingAction(undefined)
     }

     const handleToggleProcessingList = () => {
          const candidateId = item.candidat.id!

          if (!isInProcessingList) {
               addCandidatesToListeTraitement([candidateId]).then(r => {
                    setIsInProcessingList(true)
                    updateUserCounter(r.data.affectedCandidates)
                    onUpdate({ localIsInProcessingList: true })
               })
          } else {
               removeCandidatesFromListeTraitement([candidateId]).then(r => {
                    setIsInProcessingList(false)
                    updateUserCounter(-r.data.affectedCandidates)
                    onUpdate({ localIsInProcessingList: false })
               })
          }
     }

     const updateUserCounter = (affected: number) => {
          setCurrentUser(prev => {
               if (prev) {
                    return {
                         ...prev,
                         nbCandidatsDansListeTraitement: (prev.nbCandidatsDansListeTraitement || 0) + affected,
                    }
               }
               return undefined
          })
     }

     useEffect(() => {
          const el = dropdownRef.current
          if (!el) return
          const handleShow = () => setIsDropdownOpen(true)
          const handleHide = () => setIsDropdownOpen(false)
          el.addEventListener("show.bs.dropdown", handleShow)
          el.addEventListener("hide.bs.dropdown", handleHide)
          return () => {
               el.removeEventListener("show.bs.dropdown", handleShow)
               el.removeEventListener("hide.bs.dropdown", handleHide)
          }
     }, [])

     const ActionButton = ({
          type,
          label,
          icon,
          colorClass,
          onClickOverride,
          fullWidth = false,
     }: {
          type: string
          label: string
          icon: string
          colorClass: string
          onClickOverride?: () => void
          fullWidth?: boolean
     }) => {
          const isDone = doneActions.includes(type) || (type === "TRAITEMENT" && isInProcessingList)

          return (
               <div className={clsx(fullWidth ? "col-12 mt-2" : "col")}>
                    <button
                         className={clsx(
                              "btn btn-sm w-100 h-100 py-3 border border-opacity-10 position-relative transition-all",
                              isDone ? `btn-${colorClass} shadow-sm border-${colorClass}` : `btn-light-${colorClass}`
                         )}
                         onClick={onClickOverride || (() => setOngoingAction(type))}
                    >
                         {isDone && (
                              <span
                                   className="position-absolute top-0 start-100 translate-middle badge badge-circle badge-success shadow-sm border border-white border-2"
                                   style={{ zIndex: 1 }}
                              >
                                   <i className="fas fa-check text-white fs-10"></i>
                              </span>
                         )}
                         <i className={clsx(icon, "d-block mb-2 fs-3", isDone ? "text-white" : "")}></i>
                         <span className={clsx("fw-boldest text-uppercase", isDone ? "text-white" : "")} style={{ fontSize: "0.6rem" }}>
                              {label}
                         </span>
                    </button>
               </div>
          )
     }

     return (
          <div className="d-flex flex-column h-100" style={{ position: "relative", zIndex: isDropdownOpen ? 100 : 1 }}>
               {/* Infos Candidat */}
               <div className="d-flex align-items-start justify-content-between mb-5">
                    <div className="d-flex align-items-start">
                         <div className="symbol symbol-55px symbol-circle me-4 border border-2 border-gray-100 p-1 shadow-xs bg-white">
                              <img src={toAbsoluteUrl("/media/svg/avatars/blank.svg")} alt="avatar" />
                         </div>
                         <div className="d-flex flex-column">
                              <span
                                   className="text-gray-900 fw-boldest fs-5 lh-1 mb-2 cursor-pointer text-hover-primary transition-all"
                                   onClick={() => window.open(`/recrutement/candidats/search/results?candidat_ids=${item.candidat.id}&focusFirstItemAndPreviewIt=1`, "_blank")}
                              >
                                   {item.candidat.prenomNom}
                              </span>
                              <div className="d-flex flex-column gap-1">
                                   <a href={`mailto:${item.candidat.email}`} className="text-muted text-hover-primary fw-bold fs-8 d-flex align-items-center">
                                        <i className="fas fa-envelope me-2 fs-9 opacity-50"></i>
                                        {item.candidat.email}
                                   </a>
                                   <a href={`tel:${item.candidat.tel}`} className="text-muted text-hover-primary fw-bold fs-8 d-flex align-items-center">
                                        <i className="fas fa-phone me-2 fs-9 opacity-50"></i>
                                        {item.candidat.tel || "N/A"}
                                   </a>
                              </div>
                         </div>
                    </div>
                    <MyTooltip title="Consulter le CV">
                         <button className="btn btn-icon btn-sm btn-light-danger border border-danger border-opacity-10 shadow-sm mt-1" onClick={onPreviewCV}>
                              <i className="fas fa-file-pdf fs-4"></i>
                         </button>
                    </MyTooltip>
               </div>

               {/* Actions Grid */}
               <div className="row g-2 mt-auto">
                    <div className="col-12">
                         <div className="row g-2 row-cols-3">
                              <ActionButton type="REPONDEUR" label="Répondeur" icon="fas fa-phone-slash" colorClass="primary" />
                              <ActionButton type="DMS" label="DMS" icon="fas fa-phone" colorClass="success" />
                              <ActionButton type="VIVIER" label="Vivier" icon="fas fa-heart" colorClass="danger" />
                              <ActionButton type="VISIO" label="Visio" icon="fas fa-video" colorClass="warning" />
                              <ActionButton type="DOCO" label="DoCo" icon="fas fa-file-alt" colorClass="dark" />
                              <div className="col">
                                   {!isRecrutementEntame && (
                                        <div className="dropdown w-100 h-100" ref={dropdownRef}>
                                             <button
                                                  className={clsx(
                                                       "btn btn-sm w-100 h-100 py-3 border border-info border-opacity-10",
                                                       isDropdownOpen ? "bg-info text-white shadow-sm" : "btn-light-info text-info text-hover-white"
                                                  )}
                                                  data-bs-toggle="dropdown"
                                                  data-bs-boundary="viewport"
                                             >
                                                  <i className={clsx("fas fa-door-open d-block mb-2 fs-5", isDropdownOpen && "text-white")}></i>
                                                  <span className="fw-boldest text-uppercase" style={{ fontSize: "0.6rem" }}>
                                                       Recruter
                                                  </span>
                                             </button>
                                             <ul className="dropdown-menu shadow-lg border-0 fs-7">
                                                  <li>
                                                       <span className="dropdown-item cursor-pointer fw-bold py-3" onClick={() => setOngoingAction("DEMANDE_INFO_PRE_EMBAUCHE")}>
                                                            <i className="fas fa-envelope-open-text me-3 text-info"></i>Mail candidat
                                                       </span>
                                                  </li>
                                                  <li>
                                                       <span className="dropdown-item cursor-pointer fw-bold py-3" onClick={() => setOngoingAction("ENTAMER_DEMANDE_EMBAUCHE")}>
                                                            <i className="fas fa-user-edit me-3 text-info"></i>Saisie manuelle
                                                       </span>
                                                  </li>
                                             </ul>
                                        </div>
                                   )}
                              </div>
                         </div>
                    </div>
                    <ActionButton
                         type="TRAITEMENT"
                         label={isInProcessingList ? "Dans ma liste" : "À traiter"}
                         icon="fas fa-inbox"
                         colorClass="info"
                         fullWidth
                         onClickOverride={handleToggleProcessingList}
                    />
               </div>

               {/* Modals remain the same */}
               {ongoingAction === "REPONDEUR" && (
                    <MyModal title="Message répondeur" show onCloseCallback={() => setOngoingAction(undefined)} fullscreen>
                         <CandidatAddSuiviMessageRepondeurForm
                              candidat={{ id: item.candidat.id!, prenomNom: item.candidat.prenomNom!, type: CONST_CANDIDAT_TYPE_SAISI }}
                              curriculumGoogleDriveUrl={cvUrl}
                              handleSubmitCallBack={() => markAsDone("REPONDEUR")}
                         />
                    </MyModal>
               )}
               {ongoingAction === "DMS" && (
                    <MyModal title="DMS" show onCloseCallback={() => setOngoingAction(undefined)} fullscreen>
                         <CandidatAddSuiviTelForm
                              candidat={{ id: item.candidat.id!, prenomNom: item.candidat.prenomNom!, type: CONST_CANDIDAT_TYPE_SAISI }}
                              curriculumGoogleDriveUrl={cvUrl}
                              handleSubmitCallBack={() => markAsDone("DMS")}
                         />
                    </MyModal>
               )}
               {ongoingAction === "VISIO" && (
                    <MyModal title="Programmer un entretien" show onCloseCallback={() => setOngoingAction(undefined)} size="lg">
                         <ProgrammerReunion
                              candidat={{
                                   id: item.candidat.id!,
                                   prenomNom: item.candidat.prenomNom!,
                                   prenom: item.candidat.prenom!,
                                   nom: item.candidat.nom!,
                                   email: item.candidat.email!,
                                   type: CONST_CANDIDAT_TYPE_SAISI,
                              }}
                              handleSubmitCallBack={() => markAsDone("VISIO")}
                         />
                    </MyModal>
               )}
               {ongoingAction === "VIVIER" && (
                    <MyModal title="Ajout à un vivier" show onCloseCallback={() => setOngoingAction(undefined)} size="lg">
                         <CandidatAddToViviersForm
                              candidats={[item.candidat as any]}
                              onSuccessCallBack={() => markAsDone("VIVIER")}
                              onErrorCallBack={() => {}}
                              onSubmitCallBack={() => {}}
                         />
                    </MyModal>
               )}
               {ongoingAction === "DOCO" && (
                    <MyModal
                         title={<span className="fw-boldest">DoCo : {item.candidat.prenomNom}</span>}
                         show
                         onCloseCallback={() => {
                              setOngoingAction(undefined)
                         }}
                         size="lg"
                    >
                         <CandidatsRechercheResultsPreviewHelpersDocos
                              standaloneCandidat={{ id: item.candidat.id!, prenomNom: item.candidat.prenomNom!, email: item.candidat.email! }}
                              onSuccess={() => markAsDone("DOCO")} // Pass success callback
                         />
                    </MyModal>
               )}
               {ongoingAction === "ENTAMER_DEMANDE_EMBAUCHE" && (
                    <MyModal title="Dossier d'embauche" show onCloseCallback={() => setOngoingAction(undefined)} size="lg">
                         <CandidatEntamerDemandeEmbauche
                              candidat={item.candidat as any}
                              callBackOnSubmit={val => {
                                   setOngoingAction(undefined)
                                   navigate(`/recrutement/demandes-embauche/${val.consultant?.id}/details`)
                              }}
                         />
                    </MyModal>
               )}
               {ongoingAction === "DEMANDE_INFO_PRE_EMBAUCHE" && (
                    <MyModal title="Faire remplir par le candidat" show onCloseCallback={() => setOngoingAction(undefined)} size="lg">
                         <CandidatDemanderInfoPreEmbaucheParMail
                              candidat={item.candidat as any}
                              callBackOnSubmit={() => {
                                   setOngoingAction(undefined)
                                   setIsRecrutementEntame(true)
                                   onUpdate({ localIsRecrutementEntame: true })
                              }}
                         />
                    </MyModal>
               )}
          </div>
     )
}

export default Step3ManageCandidate
